import { Check, X } from "lucide-react";
import { motion } from "framer-motion";

const perfectFor = [
  "You want reliable, qualified leads coming in every week",
  "You're relying too much on referrals and want more control",
  "You're sick of tyre-kickers and want better quality enquiries",
  "You want more 5-star Google reviews without chasing customers",
  "You want a proven system that handles follow-up automatically",
];

const notFor = [
  "You don't want to grow or take on more work",
  "You prefer managing all marketing and follow-up manually",
  "You're happy relying only on word-of-mouth referrals",
  "You're not open to digital marketing systems that improve results",
  "You avoid following up on enquiries or replying to leads",
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut" as const,
    },
  },
};

export function FitSection() {
  return (
    <section id="who-we-work-with" className="section-padding bg-background relative overflow-hidden" aria-labelledby="fit-heading">
      {/* Background accents */}
      <div className="absolute top-1/4 left-0 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-primary/[0.03] rounded-full blur-[80px] sm:blur-[100px] -translate-x-1/2" />
      <div className="absolute inset-0 grid-pattern opacity-25" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-center mb-6 sm:mb-8"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Who We Work With</span>
          </div>
          <h2 id="fit-heading" className="heading-section text-foreground">
            Is Our Marketing System Right for You?
          </h2>
          <p className="text-muted-foreground text-base mt-3 max-w-xl mx-auto">
            We work with tradies, builders, construction companies and home service businesses across Sydney, Melbourne, Brisbane, Perth and Australia-wide who are serious about growth.
          </p>
        </motion.div>

        {/* Two Column Comparison */}
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-4 lg:gap-5">
          {/* Perfect For You */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            className="relative rounded-xl lg:rounded-2xl bg-gradient-to-br from-[#EAF6E5] to-[#DCF0D4] border border-[#4F9B29]/15 p-5 sm:p-6 lg:p-7 shadow-card h-full flex flex-col"
          >
            {/* Decorative accent */}
            <div className="absolute top-0 left-6 w-12 h-1 bg-[#4F9B29] rounded-b-full" />
            
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#4F9B29] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#4F9B29]/25" aria-hidden="true">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#2A4520]">Perfect For You If:</h3>
            </div>

            <motion.ul
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-2.5 flex-1"
              aria-label="Signs our tradie marketing system is right for you"
            >
              {perfectFor.map((item, index) => (
                <motion.li
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-2.5"
                >
                  <div className="w-5 h-5 rounded-full bg-[#4F9B29]/25 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <Check className="w-2.5 h-2.5 text-[#4F9B29]" strokeWidth={3} />
                  </div>
                  <span className="text-[#2A4520] leading-[1.65] text-[0.9375rem] sm:text-base font-medium">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Not For You */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
            className="relative rounded-xl lg:rounded-2xl bg-gradient-to-br from-[#FBF4F4] to-[#F5EBEB] border border-[#D9534F]/15 p-5 sm:p-6 lg:p-7 shadow-card h-full flex flex-col"
          >
            {/* Decorative accent */}
            <div className="absolute top-0 left-6 w-12 h-1 bg-[#D9534F]/50 rounded-b-full" />
            
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#D9534F] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D9534F]/25" aria-hidden="true">
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#5A3030]">Probably Not For You If:</h3>
            </div>

            <motion.ul
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-2.5 flex-1"
              aria-label="Signs our tradie marketing system may not be right for you"
            >
              {notFor.map((item, index) => (
                <motion.li
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-2.5"
                >
                  <div className="w-5 h-5 rounded-full bg-[#D9534F]/20 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <X className="w-2.5 h-2.5 text-[#D9534F]" strokeWidth={3} />
                  </div>
                  <span className="text-[#5A3030]/80 leading-[1.65] text-[0.9375rem] sm:text-base font-medium">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
